import React from "react";
import { Handle, Position, useConnection, useNodeId, type Node, type NodeProps } from "@xyflow/react";
import { FaExclamationTriangle, FaHighlighter, FaLink } from "react-icons/fa";
import { useU2000ReactFlowDialogContext } from "../dialog/U2000ReactFlowDialog";

export interface U2000ComplexNodeData extends Record<string, unknown> {
  lot: string;
  dateEstablishment: string;
  cadastre: string;
  /** Rang du cadastre (ex. rankLabel), affiché sous le cadastre. */
  rank?: string;
  operationType: string;
  dateCorrection: string | null;
  visible?: boolean;
  showCheckbox?: boolean;
  /** Indique que l’index immeuble du lot porte des annotations (icône à droite du nœud de lot). */
  indexAnnoter?: boolean;
  /** Libellé accessibilité / infobulle pour l’icône index annoté (fourni par l’appelant, ex. i18n U3141). */
  indexAnnotatedLabel?: string;
  /** Document chaîne de lots annotée présent sur le lot (U3141 : rfLotDetailAnnotatedLotChain dans documentTypes). */
  hasAnnotatedLotChain?: boolean;
  /** Libellé accessibilité / infobulle pour l’icône chaîne générée (fourni par l’appelant, ex. i18n U3141). */
  hasAnnotatedLotChainLabel?: string;
  /** Chaîne annotée non à jour sur ce nœud (U3141 : indexMostRecent sur rfLotDetailAnnotatedLotChain), indépendant de indexAnnoter. */
  annotatedChainOutdated?: boolean;
  /** Libellé accessibilité / infobulle pour l’icône chaîne non à jour (fourni par l’appelant, ex. i18n U3141). */
  annotatedChainOutdatedLabel?: string;
  nodePayload?: unknown;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  className?: string;
}

export type U2000ComplexFlowNode = Node<U2000ComplexNodeData, "u2000Complex">;

/** Même teinte que les arêtes concordance (U2000ReactFlowDialog). */
const U2000_GRAPH_HANDLE_IDLE = "rgb(37 99 235)";
const U2000_GRAPH_HANDLE_ACTIVE = "#171717";

/** Pastille visible (une seule par côté — l’autre handle est zone de clic transparente). */
const U2000_GRAPH_HANDLE_VISIBLE_CLASS = "u2000-graph-handle-visible";
const U2000_GRAPH_HANDLE_HIT_CLASS = "u2000-graph-handle-hit";

const u2000GraphHandleChromeBase: React.CSSProperties = {
  width: 12,
  height: 12,
  border: "2px solid white",
  borderRadius: "50%",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
};

/** Deuxième handle au même emplacement : hit area sans pastille (évite le double point). */
const u2000GraphHandleHitAreaStyle: React.CSSProperties = {
  width: 16,
  height: 16,
  opacity: 0,
  background: "transparent",
  border: "none",
  boxShadow: "none",
  minWidth: 16,
  minHeight: 16,
};

function useU2000GraphConnectionState() {
  const nodeId = useNodeId();
  return useConnection((c) => ({
    inProgress: c.inProgress,
    fromNodeId: c.fromNode?.id,
    fromHandle: c.fromHandle,
    toNodeId: c.toNode?.id,
    toHandle: c.toHandle,
    isValid: c.isValid,
  }));
}

function u2000GraphHandleMatches(
  h: { id?: string | null; type?: string | null } | null | undefined,
  handleId: string,
  handleType: "source" | "target",
): boolean {
  return (h?.id ?? "") === handleId && h?.type === handleType;
}

/** Pastille unique par côté : reflète l’activité source ou target au même point. */
function useU2000GraphHandleSideStyle(
  conn: ReturnType<typeof useU2000GraphConnectionState>,
  nodeId: string | null,
  handleId: string,
  handleTypes: ("source" | "target")[],
): React.CSSProperties {
  let background = U2000_GRAPH_HANDLE_IDLE;
  if (conn.inProgress && nodeId) {
    for (const handleType of handleTypes) {
      const isFrom = conn.fromNodeId === nodeId && u2000GraphHandleMatches(conn.fromHandle, handleId, handleType);
      const isTo = conn.toNodeId === nodeId && u2000GraphHandleMatches(conn.toHandle, handleId, handleType);
      if (isFrom || (isTo && conn.isValid !== false)) {
        background = U2000_GRAPH_HANDLE_ACTIVE;
        break;
      }
    }
  }

  return { ...u2000GraphHandleChromeBase, background };
}

/** Ancre cliquée / liaison en cours — repère fort sur la carte (comme sélection). */
function useU2000GraphNodeConnectionHighlight(
  conn: ReturnType<typeof useU2000GraphConnectionState>,
  nodeId: string | null,
): boolean {
  if (!conn.inProgress || !nodeId) return false;
  if (conn.fromNodeId === nodeId) return true;
  if (conn.toNodeId === nodeId && conn.isValid !== false) return true;
  return false;
}

const U2000ComplexNode: React.FC<NodeProps<U2000ComplexFlowNode>> = ({ id, data, selected }) => {
  const reactFlowContext = useU2000ReactFlowDialogContext();
  const showCheckbox = data.showCheckbox ?? reactFlowContext?.showCheckbox ?? false;
  const visible = data.visible ?? false;
  const onNodeVisibleChange = reactFlowContext?.onNodeVisibleChange;
  const disabledByRole = reactFlowContext?.checkboxDisabled ?? false;
  const disabledByAncestor = reactFlowContext?.isCheckboxDisabledByAncestor?.(id) ?? false;
  const checkboxDisabled = disabledByRole || disabledByAncestor;

  const nodeId = useNodeId();
  const conn = useU2000GraphConnectionState();
  const connectionHighlight = useU2000GraphNodeConnectionHighlight(conn, nodeId);

  const topVisibleHandleStyle = useU2000GraphHandleSideStyle(conn, nodeId, "top", ["target", "source"]);
  const bottomVisibleHandleStyle = useU2000GraphHandleSideStyle(conn, nodeId, "bottom", ["source", "target"]);

  const containerStyle: React.CSSProperties = {};
  if (data.backgroundColor) containerStyle.backgroundColor = data.backgroundColor;
  if (data.borderColor) containerStyle.borderColor = data.borderColor;
  if (data.borderWidth) containerStyle.borderWidth = data.borderWidth;
  if (data.borderRadius) containerStyle.borderRadius = data.borderRadius;

  // Checkbox décochée (visible true) → nœud grisé et descendants désactivés ; cochée (visible false) → style actif (bleu)
  const isGrayed = visible;
  const baseClasses = isGrayed
    ? "relative rounded-lg border-2 border-blue-200/50 bg-blue-50/20 p-3 shadow-xs transition-all duration-200 hover:shadow-md"
    : "relative rounded-lg border-2 border-blue-300 bg-blue-50 p-3 shadow-md transition-all duration-200 hover:shadow-lg";
  /** Survol / clic carte ; pendant une liaison, repère sur la carte (couleur des ancres gérée en CSS). */
  const cardStrongHighlight = selected || connectionHighlight;
  const interactionClasses = cardStrongHighlight
    ? "z-10 ring-4 ring-blue-600 ring-offset-2 border-blue-600 shadow-lg"
    : "hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 hover:border-blue-500";
  const containerClassName = [baseClasses, interactionClasses, data.className].filter(Boolean).join(" ");
  const textTitleClass = isGrayed ? "text-sm font-semibold text-blue-300/80 truncate" : "text-sm font-semibold text-blue-800 truncate";
  const textSubClass = isGrayed ? "text-xs text-blue-300/70 truncate" : "text-xs text-blue-700 truncate";
  const textMutedClass = isGrayed ? "text-xs text-blue-300/60 truncate" : "text-xs text-blue-600 truncate";

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (checkboxDisabled) return;
    // Inversé : coché → visible false, décoché → visible true
    onNodeVisibleChange?.(id, !e.target.checked);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={containerClassName}
      style={Object.keys(containerStyle).length > 0 ? containerStyle : undefined}
      data-selected={selected}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className={U2000_GRAPH_HANDLE_VISIBLE_CLASS}
        style={topVisibleHandleStyle}
      />
      <Handle type="source" position={Position.Top} id="top" className={U2000_GRAPH_HANDLE_HIT_CLASS} style={u2000GraphHandleHitAreaStyle} />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className={U2000_GRAPH_HANDLE_VISIBLE_CLASS}
        style={bottomVisibleHandleStyle}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom"
        className={U2000_GRAPH_HANDLE_HIT_CLASS}
        style={u2000GraphHandleHitAreaStyle}
      />
      <div className="flex items-start gap-2">
        {showCheckbox && (
          <div className="shrink-0 pt-0.5" onClick={handleCheckboxClick} onMouseDown={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={!visible}
              disabled={checkboxDisabled}
              onChange={handleCheckboxChange}
              aria-label={`Visible pour le nœud ${data.lot}`}
              className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div className={`flex min-w-0 items-center justify-between gap-2 ${textTitleClass}`}>
            <span className="min-w-0 truncate" title={data.lot}>
              {data.lot}
            </span>
            {data.hasAnnotatedLotChain || data.indexAnnoter || data.annotatedChainOutdated ? (
              <span className="flex shrink-0 items-center gap-0.5">
                {data.hasAnnotatedLotChain ? (
                  <span
                    className="text-blue-600"
                    title={data.hasAnnotatedLotChainLabel}
                    aria-label={data.hasAnnotatedLotChainLabel}
                  >
                    <FaLink className="h-4 w-4" aria-hidden />
                  </span>
                ) : null}
                {data.indexAnnoter ? (
                  <span className="text-amber-600" title={data.indexAnnotatedLabel} aria-label={data.indexAnnotatedLabel}>
                    <FaHighlighter className="h-4 w-4" aria-hidden />
                  </span>
                ) : null}
                {data.annotatedChainOutdated ? (
                  <span
                    className="text-red-600"
                    title={data.annotatedChainOutdatedLabel}
                    aria-label={data.annotatedChainOutdatedLabel}
                  >
                    <FaExclamationTriangle className="h-4 w-4" aria-hidden />
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <div className={textSubClass} title={data.dateEstablishment}>
            {data.dateEstablishment}
          </div>
          <div className={textSubClass} title={data.cadastre}>
            {data.cadastre}
          </div>
          {data.rank != null && data.rank !== "" && (
            <div className={textSubClass} title={data.rank}>
              {data.rank}
            </div>
          )}
          <div className={textMutedClass} title={data.operationType}>
            {data.operationType}
          </div>
          <div className={textMutedClass} title={data.dateCorrection ?? undefined}>
            {data.dateCorrection}
          </div>
        </div>
      </div>
    </div>
  );
};

export default U2000ComplexNode;
